import { Component, OnInit, HostListener, inject } from '@angular/core';
import { ActivatedRoute, Router, NavigationStart, RouterLink, RouterLinkActive } from '@angular/router';
import { AuthenticationService, authUserType, reRgType } from "app/services/auth.service";
import { LoaderService } from 'app/services/loader.service';
import { NgClass, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ClickStopPropagationDirective } from '../click-stop-propagation.directive';

type menuItemType = {
  title: string,
  link: string,
  icon?: string,
  class?: string,
  canSee?: () => boolean;
  callback?: (i:menuItemType) => void;
  subItems?: menuItemType[]
}

@Component({
    selector: 'nwm-menu',
    templateUrl: 'menu.component.html',
    styleUrls: ['./menu.component.scss'],
    imports: [NgClass, RouterLink, FormsModule, ClickStopPropagationDirective, NgTemplateOutlet, RouterLinkActive]
})
export class MenuComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auth = inject(AuthenticationService);
  private loaderService = inject(LoaderService);

  public user?: authUserType;
  public menuItems: menuItemType[] = [
    {title: 'Charitable Giving', link: '/pgf', canSee:()=>this.isPgf},
    {title: 'Overview', link: '/welcome', canSee:()=>this.hasPortfolio,
      callback:(i)=>{
        if (this.isPgf) {
          i.title = 'Performance';
          i.link = '/welcome/pgf';
        }
      }},
    {title: 'Portfolio',link: '/portfolio', canSee:()=>this.hasPortfolio && !this.isPgf},
    {title: 'Net Worth', link: '/net-worth', canSee: () => this.hasPortfolio},
    {title: 'Advisory Team', link: '/advisor', canSee:()=>this.hasReRg && !this.isAccountant},
    {title: 'Documents', link: '/document', canSee:()=>this.hasReRg || this.isAccountant},
    {title: 'Resources', link: '/resource', canSee:()=>!this.isAccountant && !this.isPgf},
    {title: 'Resources', link: '/pgf-resources', canSee:()=>this.isPgf},
    {title: 'Philanthropy', icon: 'volunteer_activism', link: '/pgf-intro', canSee:()=>this.hasNoPgf },
    {title: 'Notifications', icon: 'notifications', link: '/notifications', class: 'notifications-icon', canSee:()=>this.hasReRg && this.isClient },
    {title: 'Profile', icon: 'account_circle', link: '', class: 'profile-icon', subItems: [
      {title: 'Settings', link: '/profile'},
      {title: 'Support', link: '/other/support'},
      {title: 'FAQ', link: '/other/faq'},
      {title: 'Legal', link: '/profile/legal'},
      {title: 'Log Out', link: ''},
    ]},
  ];
  public preLoginItems:menuItemType[] = [
    {title: 'Log In',        link: '/account/login/', class: 'login'},
    {title: 'Meet With Us',  link: 'https://www.nicolawealth.com/meet-with-us', class: 'meet-with-us'}
  ];
  public availableMenuItems:menuItemType[] = [];
  public unAvailableLinks:string[] = [];
  public activeLink = '';
  public hasPortfolio = false;
  public hasReRg = false;
  public hasNoPgf = false;
  public isAccountant = false;
  public isClient = false;
  public isPgf = false;
  public homeLink = '/welcome';
  public logoutLink = '/account/logout';
  public menuOpened?: boolean;
  public notificationsCount = 0;
  public displayName = '';
  public rergSelectorShown = false;
  public currentReRg?: reRgType;
  public isSwitchingReRg = false;

  constructor() {
    const router = this.router;

    router.events.subscribe((val) => {
      if (val instanceof NavigationStart) {
        this.closeMenu();
      }
    });

  }

  @HostListener('document:click')
  onDocumentClick() {
    this.rergSelectorShown = false;
  }

  ngOnInit(): void {
    this.user = this.auth.getUser()!;
    const profile = this.auth.getProfile();
    if (this.user) {
      this.currentReRg = this.auth.getCurrentReRg()!;
      this.displayName = (this.user.isAccountant ? profile?.Name :
        this.currentReRg?.ClientName || profile?.Name || this.user.displayName) || ''; // added user.displayName in case user doesn't have ReRgs linked

      this.hasReRg = !!this.user.reRgs?.length;
      this.hasNoPgf = !!this.user.reRgs?.every(f=>f.RergType!=='PGF');
      this.isAccountant = this.user.isAccountant;
      this.isClient = this.user.isClient;
      this.isPgf = this.currentReRg?.RergType === 'PGF';
      this.hasPortfolio = !this.isAccountant && !!this.auth.getCurrentValuationDate();

      this.availableMenuItems = [];
      this.unAvailableLinks = [];
      this.menuItems.forEach(f=> {
        if (typeof f.canSee !== 'function' || f.canSee()) {
          if (typeof f.callback === 'function') f.callback(f);
          this.availableMenuItems.push(f);
        } else {
          this.unAvailableLinks.push(f.link);
        }
      });

      if (this.user.isClient) {
        this.auth.getNewNotificationsCount().subscribe(c => {this.notificationsCount = c});
      }

      this.homeLink = this.auth.getHomeLink();
      if (this.unAvailableLinks.includes(this.homeLink)) {
        this.homeLink = this.availableMenuItems[0].link;
      }

      const currentPath = '/'+window.location.pathname.split('/')[1];
      if (this.unAvailableLinks.includes(currentPath)) {
        this.loaderService.resetAll();
        this.router.navigate([this.availableMenuItems[0].link]);
      }

    } else {
      this.user = undefined;
      this.availableMenuItems = this.preLoginItems;
    }

    this.route.url.subscribe( () => {
      this.activeLink = window.location.pathname;
    });

    this.route.params.subscribe( params => {
      this.navigateTo(params['tag']);
    });
  }

  // for a11y, we want to make sure the rg-selector would be opened when press enter
  keyDownOnReRgSelector(event: KeyboardEvent): void {
    if(event.key === 'Enter') {
      this.rergSelectorShown = !this.rergSelectorShown;
    }
  }
  // for a11y, we want to make sure the manu would be opened when press enter
  keyDownOnMenu(event: KeyboardEvent): void {
    if(event.key === 'Enter') {
      this.menuOpened = !this.menuOpened;
    }
  }

  menuItemClick(item:menuItemType){
    if (item.title == 'Log Out') this.logOut();
    this.closeMenu();
  }

  setReRg(reRg: reRgType) {
    if (this.isSwitchingReRg) return;
    this.isSwitchingReRg = true;
    if (reRg.RergType === 'PGF' && this.activeLink === '/welcome') { // if PGF is selected and user is on the Overview page, redirect to /pgf
      sessionStorage.setItem('NavigateTo', '/pgf'); //notify app.component that we need to navigate after ReRg info loads
    }
    this.auth.setReRg(reRg).subscribe();
  }

  navigateTo(tag: string): void {
    this.loaderService.navigateTo.next(tag);
  }

  logOut(): boolean {
    this.auth.logUserOut();
    this.loaderService.resetAll();
    this.router.navigate([this.logoutLink]);
    return false;
  }

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.closeMenu();
  }

  menuToggle() {
    jQuery('body').toggleClass('forceMenu');
  }

  closeMenu() {
    jQuery('body').removeClass('forceMenu');
    this.menuOpened = false;
    return true;
  }
}
